/**
 * Service đồng bộ liên lạc giữa các tab CRM và Cửa sổ Softphone Zenify (Dedicated Popup)
 * Sử dụng BroadcastChannel API kèm fallback qua localStorage event.
 */

const CHANNEL_NAME = "edutalk_zenify_channel";
const STORAGE_KEY = "__zenify_sync_event__";

let broadcastChannel = null;

export function getZenifyChannel() {
  if (typeof window === "undefined") return null;
  if (!broadcastChannel && "BroadcastChannel" in window) {
    try {
      broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
    } catch (e) {
      console.warn("BroadcastChannel không được hỗ trợ hoặc bị chặn:", e);
    }
  }
  return broadcastChannel;
}

/**
 * Gửi message từ tab này sang tab khác / popup
 * @param {Object} payload 
 */
export function sendZenifyMessage(payload) {
  if (typeof window === "undefined") return;
  const data = {
    ...payload,
    _ts: Date.now()
  };

  const channel = getZenifyChannel();
  if (channel) {
    try {
      channel.postMessage(data);
    } catch (e) {
      console.warn("Lỗi gửi qua BroadcastChannel:", e);
    }
  }

  // Fallback qua localStorage
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // ignore
  }
}

/**
 * Lắng nghe message từ các tab khác / popup
 * @param {Function} handler Callback nhận message
 * @returns {Function} Hàm unsubscribe
 */
export function subscribeZenifyMessages(handler) {
  if (typeof window === "undefined") return () => {};

  const channel = getZenifyChannel();
  const bcListener = (event) => {
    if (event && event.data) {
      handler(event.data);
    }
  };

  if (channel) {
    channel.addEventListener("message", bcListener);
  }

  const storageListener = (event) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      try {
        const parsed = JSON.parse(event.newValue);
        handler(parsed);
      } catch (e) {}
    }
  };

  window.addEventListener("storage", storageListener);

  return () => {
    if (channel) {
      channel.removeEventListener("message", bcListener);
    }
    window.removeEventListener("storage", storageListener);
  };
}

export const ZENIFY_HEARTBEAT_KEY = "__zenify_popup_active_heartbeat__";

/**
 * Kiểm tra xem có cửa sổ Zenify nào đang hoạt động trên bất kỳ tab nào không
 */
export function isZenifyPopupActive() {
  if (typeof window === "undefined") return false;
  try {
    const ts = Number(localStorage.getItem(ZENIFY_HEARTBEAT_KEY) || 0);
    return Date.now() - ts < 3000;
  } catch (e) {
    return false;
  }
}

let popupWindowRef = null;

/**
 * Mở hoặc Focus Cửa sổ Tổng đài Zenify độc lập (Softphone Pop-up)
 * @param {String} phone Số điện thoại cần gọi (tuỳ chọn)
 */
export function openZenifyPopup(phone = "") {
  if (typeof window === "undefined") return null;

  // 1. Nếu popup đã đang hoạt động ở tab này hoặc tab khác, chỉ gửi signal FOCUS / DIAL, không mở thêm cửa sổ
  if (isZenifyPopupActive()) {
    sendZenifyMessage({ type: "FOCUS", phone });
    if (phone) {
      sendZenifyMessage({ type: "DIAL", phone });
    }
    try {
      if (popupWindowRef && !popupWindowRef.closed) {
        popupWindowRef.focus();
      }
    } catch (e) {}
    return popupWindowRef;
  }

  // 2. Thử focus window nếu tab hiện tại còn giữ ref và chưa đóng
  try {
    if (popupWindowRef && !popupWindowRef.closed) {
      popupWindowRef.focus();
      if (phone) {
        sendZenifyMessage({ type: "DIAL", phone });
      }
      return popupWindowRef;
    }
  } catch (e) {
    // Do cross-origin hoặc browser restriction
  }

  const url = "/zenify-dialer" + (phone ? `?phone=${encodeURIComponent(phone)}` : "");
  const windowName = "ZenifySoftphoneWindow";
  const width = 380;
  const height = 640;
  const left = Math.max(0, window.screen.width - width - 50);
  const top = Math.max(0, Math.floor((window.screen.height - height) / 2));
  const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,toolbar=no,menubar=no,location=no`;

  popupWindowRef = window.open(url, windowName, features);
  if (popupWindowRef) {
    try {
      popupWindowRef.focus();
    } catch (e) {}
  }

  if (phone) {
    setTimeout(() => {
      sendZenifyMessage({ type: "DIAL", phone });
    }, 600);
  }

  return popupWindowRef;
}
